import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { delay, STAGGER } from '@caios/ui';

/** Public customer pages (Wave A4b). No sign-in: the link itself is the credential. */
async function pub<T>(path: string, body?: unknown): Promise<{ ok: boolean; data: T }> {
  const r = await fetch(path, body ? { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {});
  return { ok: r.ok, data: (await r.json().catch(() => ({}))) as T };
}
function Frame({ desk, accent, children }: { desk?: string; accent?: string | null; children: React.ReactNode }) {
  return (
    <div className="cx-app auth-screen help" style={accent ? ({ ['--cx-accent']: accent } as React.CSSProperties) : undefined}>
      <section className="cx-hero-panel auth-card help-card cx-rise">
        <div className="cx-eyebrow">{desk ?? 'Support'} · <b>help</b></div>
        {children}
      </section>
    </div>
  );
}
const Gone = () => <Frame><h1 className="cx-hero">This link isn’t valid any more</h1><p className="cx-body">Reply to any of our emails and we’ll send you a fresh one.</p></Frame>;
const STATUS: Record<string, string> = { open: 'Open', pending: 'Waiting on you', resolved: 'Resolved', closed: 'Closed' };

type PortalData = { contact: { email: string; name: string | null }; desk: { name: string; accent: string | null }; tickets: { id: string; number: number; subject: string; status: string; updated_at: string }[] };
export function Portal() {
  const { token } = useParams({ from: '/help/portal/$token' });
  const q = useQuery({ queryKey: ['portal', token], queryFn: () => pub<PortalData>(`/api/v1/help/portal/${token}`) });
  const [open, setOpen] = useState<string | null>(null);
  if (q.isPending) return <Frame><p className="cx-body">Loading…</p></Frame>;
  if (!q.data?.ok) return <Gone />;
  const d = q.data.data;
  return (
    <Frame desk={d.desk.name} accent={d.desk.accent}>
      {!open ? (<>
        <h1 className="cx-hero">Hi{d.contact.name ? ` ${d.contact.name.split(' ')[0]}` : ''}, here are your requests</h1>
        <ul className="rows">{d.tickets.map((t, i) => (
          <li key={t.id} className="cx-inset row help-row cx-rise" style={delay(i, STAGGER.list)} data-testid="portal-ticket">
            <span className="cx-mono">#{t.number}</span>
            <button type="button" className="task-title" onClick={() => setOpen(t.id)}>{t.subject}</button>
            <span className="cx-label">{STATUS[t.status] ?? t.status}</span>
          </li>))}
        </ul>
      </>) : <PortalTicket token={token} id={open} onBack={() => setOpen(null)} />}
    </Frame>
  );
}

function PortalTicket({ token, id, onBack }: { token: string; id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['portal-ticket', id], queryFn: () => pub<{ ticket: { number: number; subject: string; status: string }; messages: { author_type: string; author_name: string | null; body: string; created_at: string }[] }>(`/api/v1/help/portal/${token}/tickets/${id}`) });
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  async function reply(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    const r = await pub(`/api/v1/help/portal/${token}/tickets/${id}/reply`, { body });
    if (r.ok) { setBody(''); setSent(true); qc.invalidateQueries({ queryKey: ['portal-ticket', id] }); qc.invalidateQueries({ queryKey: ['portal'] }); }
  }
  if (!q.data?.ok) return <p className="cx-body">Loading…</p>;
  const d = q.data.data;
  return (<>
    <button type="button" className="row-action" onClick={onBack}>← All requests</button>
    <h1 className="cx-hero">#{d.ticket.number} {d.ticket.subject}</h1>
    <ul className="rows thread">{d.messages.map((m, i) => (
      <li key={i} className={`cx-inset msg msg--${m.author_type}`}><b>{m.author_type === 'agent' ? 'Support team' : 'You'}</b> <span className="cx-label">{new Date(m.created_at).toLocaleString()}</span><p>{m.body}</p></li>))}
    </ul>
    <form className="auth-form" onSubmit={reply}>
      <textarea rows={4} placeholder="Write a reply" value={body} onChange={(e) => setBody(e.target.value)} data-testid="portal-reply-body" />
      <button type="submit" className="cx-btn cx-btn--primary auth-wide" data-testid="portal-reply">Send reply</button>
      {sent && <p className="ok-note">Sent. We’ll get back to you.</p>}
    </form>
  </>);
}

export function Csat() {
  const { token } = useParams({ from: '/help/csat/$token' });
  const q = useQuery({ queryKey: ['csat', token], queryFn: () => pub<{ number: number; subject: string; desk: string; csat_rating: number | null }>(`/api/v1/help/csat/${token}`) });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  if (q.isPending) return <Frame><p className="cx-body">Loading…</p></Frame>;
  if (!q.data?.ok) return <Gone />;
  const d = q.data.data;
  if (done) return <Frame desk={d.desk}><h1 className="cx-hero">Thank you</h1><p className="cx-body">Your feedback goes straight to the team.</p></Frame>;
  return (
    <Frame desk={d.desk}>
      <h1 className="cx-hero">How did we do?</h1>
      <p className="cx-body">#{d.number} {d.subject}</p>
      <div className="stars" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" role="radio" aria-checked={rating === n} className={`star${n <= rating ? ' star--on' : ''}`} onClick={() => setRating(n)} data-testid={`star-${n}`}>★</button>)}
      </div>
      <textarea rows={3} placeholder="Anything we could do better? (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
      <button type="button" className="cx-btn cx-btn--primary auth-wide" disabled={!rating} onClick={async () => { if ((await pub(`/api/v1/help/csat/${token}`, { rating, comment })).ok) setDone(true); }} data-testid="csat-submit">Send</button>
    </Frame>
  );
}

export function HelpCentre() {
  const { org, desk } = useParams({ from: '/help/$org/$desk' });
  const q = useQuery({ queryKey: ['help', org, desk], queryFn: () => pub<{ desk: { name: string; accent: string | null }; articles: { slug: string; title: string }[] }>(`/api/v1/help/${org}/${desk}`) });
  const [slug, setSlug] = useState<string | null>(null);
  const a = useQuery({ queryKey: ['help-article', org, desk, slug], enabled: !!slug, queryFn: () => pub<{ article: { title: string; body: string } }>(`/api/v1/help/${org}/${desk}/${slug}`) });
  if (q.isPending) return <Frame><p className="cx-body">Loading…</p></Frame>;
  if (!q.data?.ok) return <Frame><h1 className="cx-hero">Help centre not found</h1></Frame>;
  const d = q.data.data;
  return (
    <Frame desk={d.desk.name} accent={d.desk.accent}>
      {!slug ? (<>
        <h1 className="cx-hero">How can we help?</h1>
        <ul className="rows">{d.articles.map((x, i) => <li key={x.slug} className="cx-inset row help-row cx-rise" style={delay(i, STAGGER.list)}><button type="button" className="task-title" onClick={() => setSlug(x.slug)} data-testid="help-article">{x.title}</button></li>)}</ul>
        {!d.articles.length && <p className="cx-body">No articles yet.</p>}
      </>) : (<>
        <button type="button" className="row-action" onClick={() => setSlug(null)}>← All articles</button>
        <h1 className="cx-hero">{a.data?.data.article?.title}</h1>
        <div className="article-body">{a.data?.data.article?.body}</div>
      </>)}
    </Frame>
  );
}
